import { useState, useEffect } from "react";

// ─── 2026 Wage Data ────────────────────────────────────────────────────────────
// ─── Wage Data · Verified September 6, 2026 ───────────────────────────────────
// Fields: min = full minimum wage | tip = minimum cash wage for tipped employees
//         credit = maximum tip credit | ok = tip credit permitted
// v: "2026-09-06" = confirmed against primary/law-firm sources this session
// v: null          = NOT verified since January 2026 — treat as stale
export const VERIFIED_ON = "September 6, 2026";

const SD = {
  AL: { name:"Alabama", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },

  // CORRECTED: was 11.91. Ballot Measure 1 → $14.00 eff. July 1, 2026.
  AK: { name:"Alaska", min:14.00, tip:14.00, credit:0, ok:false, v:"2026-09-06",
    note:"Alaska prohibits tip credits. Minimum wage rises to $15.00 on July 1, 2027." },

  AZ: { name:"Arizona", min:14.70, tip:11.70, credit:3.00, ok:true, v:null,
    locs:[
      { name:"Flagstaff", min:17.85, tip:14.85, credit:3.00, ok:true },
      { name:"Tucson", min:15.00, tip:12.00, credit:3.00, ok:true },
    ]},

  AR: { name:"Arkansas", min:11.00, tip:2.63, credit:8.37, ok:true, v:null },

  // CORRECTED localities: July 1, 2026 increases applied.
  CA: { name:"California", min:16.90, tip:16.90, credit:0, ok:false, v:"2026-09-06",
    note:"CA prohibits tip credits. All employees receive full minimum wage before tips.",
    locs:[
      { name:"Los Angeles (City)", min:18.47, tip:18.47, credit:0, ok:false },
      { name:"Los Angeles County (unincorporated)", min:18.47, tip:18.47, credit:0, ok:false },
      { name:"San Francisco", min:19.61, tip:19.61, credit:0, ok:false },
      { name:"Pasadena", min:18.57, tip:18.57, credit:0, ok:false },
      { name:"Santa Monica", min:18.47, tip:18.47, credit:0, ok:false },
      { name:"Malibu", min:17.91, tip:17.91, credit:0, ok:false },
      { name:"Milpitas", min:18.50, tip:18.50, credit:0, ok:false },
      { name:"West Hollywood", min:20.25, tip:20.25, credit:0, ok:false },
      { name:"San Diego", min:17.75, tip:17.75, credit:0, ok:false },
    ]},
  // NOTE: Alameda, Berkeley, Emeryville, and Fremont also increased July 1, 2026.
  // Rates not confirmed — removed rather than published stale. Add once verified.

  CO: { name:"Colorado", min:14.81, tip:11.79, credit:3.02, ok:true, v:null,
    locs:[
      { name:"Denver", min:18.29, tip:15.27, credit:3.02, ok:true },
      { name:"Boulder", min:15.57, tip:12.55, credit:3.02, ok:true },
    ]},

  CT: { name:"Connecticut", min:16.94, tip:10.71, credit:6.23, ok:true, v:null,
    note:"CT requires tipped employees sign a written attestation each pay period confirming tips reached minimum wage. Missing this invalidates the tip credit for that period." },

  DE: { name:"Delaware", min:15.00, tip:2.23, credit:12.77, ok:true, v:null },

  // CORRECTED — this was the most serious error in the file.
  // Old data said DC eliminated the tip credit. It did not.
  // The FY2026 Budget Support Act paused and recalibrated Initiative 82.
  DC: { name:"Washington D.C.", min:18.40, tip:10.30, credit:8.10, ok:true, v:"2026-09-06",
    note:"Initiative 82 was amended by the FY2026 Budget Support Act. The tip credit was NOT eliminated. As of July 1, 2026 the tipped cash wage is $10.30 (56% of the full minimum). The phase-out now runs to 2034, with the tip credit ultimately capped at 25% of minimum wage. Employer must make up any shortfall to $18.40." },

  // Correct until September 30, 2026 — then $15.00 / $11.98.
  FL: { name:"Florida", min:14.00, tip:11.00, credit:3.00, ok:true, v:"2026-09-06",
    note:"⚠ Rate changes September 30, 2026: minimum wage $15.00, tipped cash wage $11.98, tip credit $3.02. Update before that date." },

  GA: { name:"Georgia", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },

  HI: { name:"Hawaii", min:14.00, tip:12.75, credit:1.25, ok:true, v:null,
    note:"HI tip credit requires employees receive tips of at least $7.00/hr above the applicable minimum wage." },

  ID: { name:"Idaho", min:7.25, tip:3.35, credit:3.90, ok:true, v:null },

  // CORRECTED: Chicago and Cook County both increased July 1, 2026.
  IL: { name:"Illinois", min:14.00, tip:8.40, credit:5.60, ok:true, v:"2026-09-06",
    locs:[
      { name:"Chicago", min:17.05, tip:12.96, credit:4.09, ok:true,
        note:"Applies to employers with 4+ employees. City Council delayed the tip-credit phase-out in May 2026 — the credit stays at 24% of minimum wage through June 30, 2028, then phases to full minimum by July 1, 2030." },
      { name:"Cook County (unincorporated)", min:15.40, tip:9.25, credit:6.15, ok:true,
        note:"Does not apply inside Chicago. Over 80% of Cook County municipalities have opted out — confirm your specific municipality." },
    ]},

  IN: { name:"Indiana", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  IA: { name:"Iowa", min:7.25, tip:4.35, credit:2.90, ok:true, v:null },
  KS: { name:"Kansas", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  KY: { name:"Kentucky", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  LA: { name:"Louisiana", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },

  ME: { name:"Maine", min:14.65, tip:7.33, credit:7.32, ok:true, v:null,
    locs:[ { name:"Portland", min:15.00, tip:7.50, credit:7.50, ok:true } ]},

  // CORRECTED — all four localities were wrong.
  // Montgomery County is tiered by employer size and was published as a single
  // flat $17.15 (the July 2024 large-employer rate). Now split into three entries.
  MD: { name:"Maryland", min:15.00, tip:3.63, credit:11.37, ok:true, v:"2026-09-06",
    note:"State minimum wage is frozen at $15.00 — the Fair Wage Act eliminated future scheduled increases. Employers must provide a Tip Credit Wage Statement each pay period.",
    locs:[
      { name:"Montgomery County — 51+ employees", min:18.00, tip:4.00, credit:14.00, ok:true,
        note:"County tipped cash wage is $4.00, higher than the state's $3.63. Adjusts each July 1 by CPI-W. Quarterly tipped wage report due to the Office of Human Rights within 30 days of quarter end (Code § 27-69(d))." },
      { name:"Montgomery County — 11–50 employees", min:16.50, tip:4.00, credit:12.50, ok:true,
        note:"Certain non-profits and Medicaid-funded home care providers use this mid-size rate even above 51 employees." },
      { name:"Montgomery County — 10 or fewer employees", min:15.95, tip:4.00, credit:11.95, ok:true },
      { name:"Prince George's County", min:15.30, tip:3.63, credit:11.67, ok:true,
        note:"Eff. January 1, 2026. Tipped cash wage follows the state at $3.63 — not $4.00. Next adjustment January 1, 2027." },
      { name:"Howard County", min:16.00, tip:3.63, credit:12.37, ok:true,
        note:"Eff. July 1, 2026 — employer-size tiers eliminated; one rate for all employers." },
      { name:"Baltimore City", min:15.00, tip:3.63, credit:11.37, ok:true,
        note:"Baltimore City does not maintain a separate local minimum wage above the state rate." },
    ]},

  MA: { name:"Massachusetts", min:15.00, tip:6.75, credit:8.25, ok:true, v:null },

  MI: { name:"Michigan", min:12.48, tip:4.74, credit:7.74, ok:true, v:null,
    note:"UNVERIFIED. Michigan's tipped schedule was restructured by SB8 (2025) and steps up annually. Confirm with Michigan LARA before relying on this." },

  MN: { name:"Minnesota", min:11.13, tip:11.13, credit:0, ok:false, v:null,
    note:"No tip credit in Minnesota. Minneapolis and St. Paul set higher local rates." },

  MS: { name:"Mississippi", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  MO: { name:"Missouri", min:15.00, tip:7.50, credit:7.50, ok:true, v:null },
  MT: { name:"Montana", min:10.55, tip:10.55, credit:0, ok:false, v:null },
  NE: { name:"Nebraska", min:15.00, tip:2.13, credit:12.87, ok:true, v:null },
  NV: { name:"Nevada", min:12.00, tip:12.00, credit:0, ok:false, v:null,
    note:"Nevada prohibits tip credits." },
  NH: { name:"New Hampshire", min:7.25, tip:3.27, credit:3.98, ok:true, v:null },

  NJ: { name:"New Jersey", min:15.49, tip:5.62, credit:9.87, ok:true, v:null,
    note:"NJ prohibits deducting credit card processing fees from employee tips. Make-up pay calculated on strict 7-day workweek, not biweekly average." },

  NM: { name:"New Mexico", min:12.00, tip:3.00, credit:9.00, ok:true, v:null,
    locs:[
      { name:"Albuquerque", min:12.00, tip:7.20, credit:4.80, ok:true },
      { name:"Santa Fe", min:14.60, tip:14.60, credit:0, ok:false },
      { name:"Las Cruces", min:12.00, tip:10.20, credit:1.80, ok:true },
    ]},

  // CORRECTED — two errors here. Rates were stale (2025), AND the file used
  // NY's "service employee" rate instead of the "food service worker" rate.
  // For a restaurant tool, food service worker is the correct category.
  // State-level default is now "Remainder of NYS"; downstate is a locality.
  NY: { name:"New York", min:16.00, tip:10.70, credit:5.30, ok:true, v:"2026-09-06",
    note:"Rates shown are for FOOD SERVICE WORKERS (servers, bartenders, bussers, runners). NY sets a separate, higher cash wage for 'service employees' in non-food tipped roles: $13.30 cash / $2.70 credit upstate, $14.15 / $2.85 downstate. Fast food employers cannot take a tip credit at all.",
    locs:[
      { name:"New York City", min:17.00, tip:11.35, credit:5.65, ok:true },
      { name:"Long Island / Westchester", min:17.00, tip:11.35, credit:5.65, ok:true },
    ]},

  NC: { name:"North Carolina", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  ND: { name:"North Dakota", min:7.25, tip:4.86, credit:2.39, ok:true, v:null },
  OH: { name:"Ohio", min:10.70, tip:5.35, credit:5.35, ok:true, v:null },
  OK: { name:"Oklahoma", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },

  // CORRECTED: July 1, 2026 three-tier increase.
  OR: { name:"Oregon", min:15.55, tip:15.55, credit:0, ok:false, v:"2026-09-06",
    note:"Oregon prohibits tip credits statewide. Rates adjust every July 1 by CPI.",
    locs:[
      { name:"Portland Metro (urban growth boundary)", min:16.80, tip:16.80, credit:0, ok:false },
      { name:"Non-urban Counties", min:14.55, tip:14.55, credit:0, ok:false },
    ]},

  PA: { name:"Pennsylvania", min:7.25, tip:2.83, credit:4.42, ok:true, v:null,
    note:"Pennsylvania state law preempts local minimum wage ordinances." },

  RI: { name:"Rhode Island", min:16.00, tip:3.89, credit:12.11, ok:true, v:null },
  SC: { name:"South Carolina", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  SD: { name:"South Dakota", min:11.85, tip:5.93, credit:5.92, ok:true, v:null },
  TN: { name:"Tennessee", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  TX: { name:"Texas", min:7.25, tip:2.13, credit:5.12, ok:true, v:"2026-09-06",
    note:"Texas follows the federal minimum wage and federal tip credit rules. State law preempts local minimum wage ordinances." },
  UT: { name:"Utah", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
  VT: { name:"Vermont", min:14.42, tip:7.21, credit:7.21, ok:true, v:null },
  VA: { name:"Virginia", min:12.77, tip:2.13, credit:10.64, ok:true, v:null },

  WA: { name:"Washington", min:17.13, tip:17.13, credit:0, ok:false, v:null,
    note:"Washington prohibits tip credits. Tips are in addition to full minimum wage. Seattle, Tukwila, Renton and Everett set higher local rates — verify current figures.",
    locs:[
      { name:"Seattle", min:21.30, tip:21.30, credit:0, ok:false },
      { name:"Tukwila", min:21.65, tip:21.65, credit:0, ok:false },
      { name:"SeaTac (Hospitality/Transport)", min:20.74, tip:20.74, credit:0, ok:false },
    ]},

  WV: { name:"West Virginia", min:8.75, tip:2.62, credit:6.13, ok:true, v:null },
  WI: { name:"Wisconsin", min:7.25, tip:2.33, credit:4.92, ok:true, v:null },
  WY: { name:"Wyoming", min:7.25, tip:2.13, credit:5.12, ok:true, v:null },
};

const stateList = Object.entries(SD)
  .sort((a,b) => a[1].name.localeCompare(b[1].name))
  .map(([code, d]) => ({ code, name: d.name }));

const $ = (n) => n != null ? `$${Number(n).toFixed(2)}` : "—";

// ─── Input Component ──────────────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase", color:"#94A3B8", marginBottom:6, fontFamily:"'IBM Plex Mono', monospace" }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize:11, color:"#64748B", marginTop:4, fontFamily:"'IBM Plex Mono', monospace" }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width:"100%", padding:"10px 14px", background:"#0D1321", border:"1.5px solid #2D3748",
  borderRadius:8, color:"#F1F5F9", fontSize:15, fontFamily:"'IBM Plex Mono', monospace",
  outline:"none", boxSizing:"border-box", appearance:"none",
};

const selectStyle = { ...inputStyle, cursor:"pointer", backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394A3B8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center", paddingRight:36 };

// ─── Check Badge ─────────────────────────────────────────────────────────────
function CheckItem({ item }) {
  const cfg = {
    pass:  { bg:"rgba(16,185,129,0.08)", border:"rgba(16,185,129,0.25)", icon:"✓", iconColor:"#10B981", labelColor:"#10B981" },
    fail:  { bg:"rgba(239,68,68,0.08)",  border:"rgba(239,68,68,0.25)",  icon:"✕", iconColor:"#EF4444", labelColor:"#EF4444" },
    warn:  { bg:"rgba(245,158,11,0.08)", border:"rgba(245,158,11,0.25)", icon:"!", iconColor:"#F59E0B", labelColor:"#F59E0B" },
    info:  { bg:"rgba(99,102,241,0.08)", border:"rgba(99,102,241,0.25)", icon:"i", iconColor:"#818CF8", labelColor:"#818CF8" },
  }[item.status] || {};
  return (
    <div style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10, display:"flex", gap:14 }}>
      <div style={{ width:24, height:24, borderRadius:"50%", background:cfg.border, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
        <span style={{ color:cfg.iconColor, fontWeight:700, fontSize:13, fontFamily:"'IBM Plex Mono', monospace" }}>{cfg.icon}</span>
      </div>
      <div>
        <div style={{ fontWeight:700, fontSize:13, color:cfg.labelColor, marginBottom:4, fontFamily:"'Syne', sans-serif", letterSpacing:"0.02em" }}>{item.label}</div>
        <div style={{ fontSize:13, color:"#CBD5E1", lineHeight:1.6, fontFamily:"'IBM Plex Mono', monospace" }}>{item.detail}</div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TipComplianceChecker({ defaultState = "" }) {
  const [selState, setSelState] = useState(defaultState);
  const [selLoc, setSelLoc] = useState("");
  const [cashWage, setCashWage] = useState("");
  const [regHrs, setRegHrs] = useState("40");
  const [otHrs, setOtHrs] = useState("0");
  const [weeklyTips, setWeeklyTips] = useState("");
  const [ntPct, setNtPct] = useState("0");
  const [hasTipPool, setHasTipPool] = useState("no");
  const [bohInPool, setBohInPool] = useState("no");
  const [numEmps, setNumEmps] = useState("1");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const stateData = selState ? SD[selState] : null;
  const localities = stateData?.locs || [];

  useEffect(() => { setSelLoc(""); setResult(null); }, [selState]);

  const getJx = () => {
    if (!stateData) return null;
    if (selLoc && localities.length) {
      const loc = localities.find(l => l.name === selLoc);
      if (loc) return { ...stateData, ...loc, displayName:`${loc.name}, ${stateData.name}`, note: loc.note || stateData.note };
    }
    return { ...stateData, displayName: stateData.name };
  };

  const handleCheck = () => {
    setError("");
    const jx = getJx();
    if (!jx) { setError("Please select a state."); return; }
    const cw = parseFloat(cashWage);
    if (isNaN(cw) || cw < 0) { setError("Enter a valid cash wage."); return; }
    const rh = parseFloat(regHrs) || 0;
    const oh = parseFloat(otHrs) || 0;
    const tips = parseFloat(weeklyTips) || 0;
    const nt = parseFloat(ntPct) || 0;
    const emps = parseInt(numEmps) || 1;
    const totalHrs = rh + oh;
    const avgHrlyTips = totalHrs > 0 ? tips / totalHrs : 0;
    const tipOk = jx.ok !== false;
    const reqCashWage = tipOk ? jx.tip : jx.min;
    const claimingCredit = tipOk && cw < jx.min;
    const checks = [];

    // 1. Tip credit eligibility (no-credit states)
    if (!tipOk) {
      checks.push({ id:"tc_ban", label:"Tip Credit Prohibited",
        status: cw >= jx.min ? "pass" : "fail",
        detail: cw >= jx.min
          ? `${jx.displayName} bans tip credits. You're paying the full minimum wage of ${$(jx.min)}/hr. ✓`
          : `${jx.displayName} prohibits tip credits. You must pay ${$(jx.min)}/hr. Currently: ${$(cw)}/hr — short by ${$(jx.min - cw)}/hr (${$(( jx.min - cw) * totalHrs)}/wk per employee).`
      });
    }

    // 2. Cash wage floor
    const cwOk = cw >= reqCashWage;
    checks.push({ id:"cw_floor", label:"Minimum Cash Wage",
      status: cwOk ? "pass" : "fail",
      detail: cwOk
        ? `${$(cw)}/hr ≥ required ${tipOk ? "tipped minimum" : "minimum"} of ${$(reqCashWage)}/hr. ✓`
        : `Required: ${$(reqCashWage)}/hr — Paying: ${$(cw)}/hr — Shortfall: ${$(reqCashWage - cw)}/hr (${$((reqCashWage - cw) * totalHrs * emps)}/wk across ${emps} employee${emps>1?"s":""}).`
    });

    // 3. Tip makeup test
    if (tipOk && claimingCredit && totalHrs > 0) {
      const eff = cw + avgHrlyTips;
      const mkOk = eff >= jx.min;
      const shortfall = Math.max(0, jx.min - eff);
      checks.push({ id:"makeup", label:"Minimum Wage — Tip Makeup Test",
        status: mkOk ? "pass" : "fail",
        detail: mkOk
          ? `${$(cw)}/hr cash + ${$(avgHrlyTips)}/hr avg tips = ${$(eff)}/hr. Meets the ${$(jx.min)}/hr minimum. No makeup pay required. ✓`
          : `${$(cw)}/hr cash + ${$(avgHrlyTips)}/hr avg tips = ${$(eff)}/hr. Below the ${$(jx.min)}/hr minimum. Makeup pay owed: ${$(shortfall)}/hr (${$(shortfall * totalHrs * emps)}/wk for ${emps} employee${emps>1?"s":""}).`
      });
    }

    // 4. Overtime rate reference
    if (oh > 0) {
      const otRate = claimingCredit
        ? (jx.min * 1.5) - jx.credit
        : cw * 1.5;
      checks.push({ id:"ot", label:"Overtime Cash Wage Rate",
        status: "info",
        detail: claimingCredit
          ? `Correct OT cash rate: (${$(jx.min)} × 1.5) − ${$(jx.credit)} tip credit = ${$(otRate)}/hr for the ${oh} OT hour${oh>1?"s":""}. ⚠ Common mistake: multiplying tipped wage × 1.5 is a DOL violation.`
          : `OT rate (no tip credit): ${$(cw)} × 1.5 = ${$(otRate)}/hr for ${oh} OT hour${oh>1?"s":""}.`
      });
    }

    // 5. 80/20 Rule
    if (tipOk && claimingCredit && nt > 0) {
      const ruleStatus = nt > 20 ? "fail" : nt > 15 ? "warn" : "pass";
      checks.push({ id:"8020", label:"80/20 Rule — Non-Tipped Work",
        status: ruleStatus,
        detail: ruleStatus === "fail"
          ? `${nt}% of shift is non-tipped work — over the 20% limit. Full minimum wage (${$(jx.min)}/hr) must be paid for those hours. This is a top DOL audit trigger in restaurant enforcement actions.`
          : ruleStatus === "warn"
          ? `${nt}% non-tipped work — approaching the 20% threshold. Document task time by shift in case of audit.`
          : `${nt}% non-tipped work — within the 20% allowance. Maintain per-shift task logs. ✓`
      });
    }

    // 6. Tip pool
    if (hasTipPool === "yes") {
      const bohViolation = bohInPool === "yes" && tipOk && claimingCredit;
      checks.push({ id:"pool", label:"Tip Pool Composition",
        status: bohViolation ? "fail" : "pass",
        detail: bohViolation
          ? "Tip pool includes back-of-house staff while claiming a tip credit. Federal violation — BOH (cooks, dishwashers) can only join tip pools when the employer pays full minimum wage to ALL employees and takes no tip credit."
          : bohInPool === "yes"
          ? "BOH employees in pool is permissible because you pay full minimum wage (no tip credit claimed). ✓"
          : "Front-of-house only tip pool is always permitted. ✓"
      });
    }

    // State/locality note
    if (jx.note) {
      checks.push({ id:"note", label:"Jurisdiction-Specific Alert",
        status: "warn",
        detail: jx.note
      });
    }

    // Exposure calc
    let weeklyExp = 0;
    if (!cwOk) weeklyExp += (reqCashWage - cw) * totalHrs * emps;
    if (tipOk && claimingCredit && totalHrs > 0) {
      const eff = cw + avgHrlyTips;
      if (eff < jx.min) weeklyExp += Math.max(0, jx.min - eff) * totalHrs * emps;
    }
    const fails = checks.filter(c => c.status === "fail");
    const warns = checks.filter(c => c.status === "warn");
    const overallStatus = fails.length > 0 ? "fail" : warns.length > 0 ? "warn" : "pass";

    setResult({ status:overallStatus, jx, checks, weeklyExp, annualExp:weeklyExp*52, emps, reqCashWage, tipOk, claimingCredit });
    setTimeout(() => document.getElementById("tcc-results")?.scrollIntoView({ behavior:"smooth", block:"start" }), 50);
  };

  const sectionStyle = { background:"#141B2D", border:"1px solid #1E293B", borderRadius:14, padding:"24px 24px 20px", marginBottom:16 };
  const sectionTitle = { fontFamily:"'Syne', sans-serif", fontWeight:700, fontSize:14, letterSpacing:"0.1em", textTransform:"uppercase", color:"#475569", marginBottom:18 };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        select option { background: #141B2D; }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
        input[type=number] { -moz-appearance: textfield; }
        .tcc-btn { transition: all 0.18s; }
        .tcc-btn:hover { background: #D97706 !important; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(245,158,11,0.35) !important; }
        .tcc-btn:active { transform: translateY(0); }
      `}</style>
      <div style={{ minHeight:"100vh", background:"#0D1321", padding:"0 0 60px", fontFamily:"'Syne', sans-serif" }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg, #0D1321 0%, #141B2D 100%)", borderBottom:"1px solid #1E293B", padding:"36px 32px 28px" }}>
          <div style={{ maxWidth:720, margin:"0 auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:8, height:8, background:"#F59E0B", borderRadius:"50%" }}/>
              <span style={{ fontSize:11, fontWeight:600, letterSpacing:"0.12em", textTransform:"uppercase", color:"#F59E0B", fontFamily:"'IBM Plex Mono', monospace" }}>2026 Rate Database · All 50 States + D.C.</span>
            </div>
            <h1 style={{ fontFamily:"'Syne', sans-serif", fontSize:32, fontWeight:800, color:"#F8FAFC", margin:"0 0 10px", lineHeight:1.15 }}>Tipped Employee Wage<br/>Compliance Checker</h1>
            <p style={{ color:"#64748B", fontSize:14, margin:0, fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
              Enter your jurisdiction and pay setup. Get an instant compliance verdict — cash wage, tip makeup, overtime, 80/20 rule, and tip pool eligibility.
            </p>
          </div>
        </div>

        <div style={{ maxWidth:720, margin:"0 auto", padding:"28px 32px 0" }}>

          {/* Section 1: Location */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>① Location</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <Field label="State">
                <select style={selectStyle} value={selState} onChange={e => setSelState(e.target.value)}>
                  <option value="">Select state…</option>
                  {stateList.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="City / Locality" hint={localities.length ? `${localities.length} local rate${localities.length>1?"s":""} available` : selState ? "No local rates for this state" : ""}>
                <select style={{ ...selectStyle, opacity: localities.length ? 1 : 0.4, cursor: localities.length ? "pointer" : "default" }}
                  value={selLoc} onChange={e => { setSelLoc(e.target.value); setResult(null); }} disabled={!localities.length}>
                  <option value="">{localities.length ? "Statewide rate" : "N/A"}</option>
                  {localities.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
                </select>
              </Field>
            </div>
            {stateData && (
              <div style={{ marginTop:12, padding:"12px 16px", background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:8, display:"flex", gap:24, flexWrap:"wrap" }}>
                {[
                  ["Min Wage", $(stateData.min)],
                  ["Tipped Min", stateData.ok ? $(stateData.tip) : "N/A"],
                  ["Max Tip Credit", stateData.ok ? $(stateData.credit) : "NONE"],
                  ["Tip Credit", stateData.ok ? "✓ Allowed" : "✗ Prohibited"],
                ].map(([k,v]) => (
                  <div key={k}>
                    <div style={{ fontSize:10, color:"#64748B", fontFamily:"'IBM Plex Mono', monospace", letterSpacing:"0.08em", textTransform:"uppercase" }}>{k}</div>
                    <div style={{ fontSize:16, fontWeight:700, color: k==="Tip Credit" ? (stateData.ok ? "#10B981" : "#EF4444") : "#F59E0B", fontFamily:"'IBM Plex Mono', monospace" }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 2: Pay Setup */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>② Your Current Pay Setup</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <Field label="Cash Wage You Pay ($/hr)" hint="The direct wage before tips">
                <input type="number" style={inputStyle} step="0.01" min="0" placeholder="e.g. 2.13" value={cashWage} onChange={e => { setCashWage(e.target.value); setResult(null); }} />
              </Field>
              <Field label="Avg Weekly Tips Per Employee ($)" hint="Total tips received per employee">
                <input type="number" style={inputStyle} step="0.01" min="0" placeholder="e.g. 420.00" value={weeklyTips} onChange={e => { setWeeklyTips(e.target.value); setResult(null); }} />
              </Field>
              <Field label="Regular Hours / Week">
                <input type="number" style={inputStyle} step="0.5" min="0" max="40" value={regHrs} onChange={e => { setRegHrs(e.target.value); setResult(null); }} />
              </Field>
              <Field label="Overtime Hours / Week" hint="Hours worked beyond 40">
                <input type="number" style={inputStyle} step="0.5" min="0" value={otHrs} onChange={e => { setOtHrs(e.target.value); setResult(null); }} />
              </Field>
              <Field label="Non-Tipped Work (% of shift)" hint="Sidework, cleaning, prep — 80/20 Rule applies">
                <input type="number" style={inputStyle} step="1" min="0" max="100" value={ntPct} onChange={e => { setNtPct(e.target.value); setResult(null); }} />
              </Field>
              <Field label="Number of Tipped Employees" hint="For total exposure calculation">
                <input type="number" style={inputStyle} step="1" min="1" value={numEmps} onChange={e => { setNumEmps(e.target.value); setResult(null); }} />
              </Field>
            </div>
          </div>

          {/* Section 3: Tip Pool */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>③ Tip Pool (Optional)</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <Field label="Do You Have a Tip Pool?">
                <select style={selectStyle} value={hasTipPool} onChange={e => { setHasTipPool(e.target.value); setResult(null); }}>
                  <option value="no">No tip pool</option>
                  <option value="yes">Yes, we pool tips</option>
                </select>
              </Field>
              {hasTipPool === "yes" && (
                <Field label="Pool Includes Back-of-House?" hint="Cooks, dishwashers, etc.">
                  <select style={selectStyle} value={bohInPool} onChange={e => { setBohInPool(e.target.value); setResult(null); }}>
                    <option value="no">No — front-of-house only</option>
                    <option value="yes">Yes — includes BOH staff</option>
                  </select>
                </Field>
              )}
            </div>
          </div>

          {error && <div style={{ color:"#EF4444", fontSize:13, marginBottom:12, fontFamily:"'IBM Plex Mono', monospace" }}>{error}</div>}

          <button className="tcc-btn" onClick={handleCheck}
            style={{ width:"100%", padding:"16px", background:"#F59E0B", border:"none", borderRadius:12, color:"#0D1321", fontSize:16, fontWeight:800, fontFamily:"'Syne', sans-serif", cursor:"pointer", letterSpacing:"0.04em", boxShadow:"0 4px 16px rgba(245,158,11,0.25)" }}>
            CHECK COMPLIANCE →
          </button>

          {/* Results */}
          {result && (
            <div id="tcc-results" style={{ marginTop:28 }}>
              {/* Verdict banner */}
              {(() => {
                const cfg = {
                  fail: { bg:"rgba(239,68,68,0.1)", border:"#EF4444", label:"COMPLIANCE ISSUES FOUND", sub:"Address violations immediately to avoid DOL liability.", color:"#EF4444" },
                  warn: { bg:"rgba(245,158,11,0.1)", border:"#F59E0B", label:"REVIEW RECOMMENDED", sub:"Some items require your attention before next payroll.", color:"#F59E0B" },
                  pass: { bg:"rgba(16,185,129,0.1)", border:"#10B981", label:"COMPLIANT", sub:"Your current setup meets applicable wage and tip credit requirements.", color:"#10B981" },
                }[result.status];
                return (
                  <div style={{ background:cfg.bg, border:`2px solid ${cfg.border}`, borderRadius:16, padding:"24px 28px", marginBottom:20, textAlign:"center" }}>
                    <div style={{ fontSize:36, fontWeight:800, color:cfg.color, fontFamily:"'Syne', sans-serif", letterSpacing:"0.04em" }}>{cfg.label}</div>
                    <div style={{ color:"#94A3B8", fontSize:14, marginTop:6, fontFamily:"'IBM Plex Mono', monospace" }}>{result.jx.displayName} · {cfg.sub}</div>
                    {result.weeklyExp > 0 && (
                      <div style={{ marginTop:16, padding:"12px 20px", background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:10, display:"inline-block" }}>
                        <span style={{ color:"#F1F5F9", fontSize:13, fontFamily:"'IBM Plex Mono', monospace" }}>
                          Estimated exposure: <strong style={{ color:"#EF4444", fontSize:16 }}>{$(result.weeklyExp)}/week</strong> · <strong style={{ color:"#EF4444" }}>{$(result.annualExp)}/year</strong> for {result.emps} employee{result.emps>1?"s":""}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── Layer 1: Contextual Affiliate CTA ── */}
              {(() => {
                const GUSTO_URL = "https://gusto.pxf.io/X4nKG5"; // ← swap with your link
                const cfgCta = {
                  fail: {
                    bg: "rgba(239,68,68,0.07)", border: "rgba(239,68,68,0.2)",
                    icon: "⚠️",
                    headline: "Fix these violations before your next payroll run.",
                    body: "Payroll platforms like Gusto automatically enforce tipped wage floors, calculate makeup pay, and flag overtime errors — so you're protected every pay period without doing this math by hand.",
                    cta: "See how Gusto handles tipped payroll →",
                    ctaBg: "#EF4444",
                  },
                  warn: {
                    bg: "rgba(245,158,11,0.07)", border: "rgba(245,158,11,0.2)",
                    icon: "🔔",
                    headline: "Your setup has risk areas worth addressing.",
                    body: "A modern payroll platform automatically updates tipped wage rates when state laws change and keeps your records audit-ready. Gusto is built for exactly this.",
                    cta: "Explore Gusto for restaurant payroll →",
                    ctaBg: "#F59E0B",
                  },
                  pass: {
                    bg: "rgba(16,185,129,0.07)", border: "rgba(16,185,129,0.2)",
                    icon: "✅",
                    headline: "You're compliant — keep it that way as rates change.",
                    body: "Tipped wage rates update annually across dozens of states and localities. Gusto automatically applies new rates when they take effect so you don't have to track them manually.",
                    cta: "See how Gusto automates wage compliance →",
                    ctaBg: "#10B981",
                  },
                }[result.status];
                return (
                  <div style={{ background: cfgCta.bg, border: `1px solid ${cfgCta.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 16, display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{ fontSize: 24, flexShrink: 0, marginTop: 2 }}>{cfgCta.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, color: "#F1F5F9", marginBottom: 6 }}>{cfgCta.headline}</div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#94A3B8", lineHeight: 1.7, marginBottom: 12 }}>{cfgCta.body}</div>
                      <a href={GUSTO_URL} target="_blank" rel="noopener noreferrer sponsored"
                        style={{ display: "inline-block", padding: "9px 18px", background: cfgCta.ctaBg, borderRadius: 8, color: "#0D1321", fontSize: 12, fontWeight: 700, fontFamily: "'Syne', sans-serif", textDecoration: "none", letterSpacing: "0.03em" }}>
                        {cfgCta.cta}
                      </a>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "#475569", marginLeft: 10 }}>Sponsored</span>
                    </div>
                  </div>
                );
              })()}

              {/* Rate reference */}
              <div style={{ ...sectionStyle, marginBottom:16 }}>
                <div style={sectionTitle}>Rate Reference — {result.jx.displayName}</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12 }}>
                  {[
                    ["Required Min Wage", $(result.jx.min)],
                    ["Required Cash Wage", $(result.reqCashWage)],
                    ["Your Cash Wage", $(parseFloat(cashWage))],
                    ["Max Tip Credit", result.tipOk ? $(result.jx.credit) : "N/A"],
                  ].map(([k,v]) => (
                    <div key={k} style={{ background:"#0D1321", borderRadius:10, padding:"14px 16px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:"#64748B", fontFamily:"'IBM Plex Mono', monospace", letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:6 }}>{k}</div>
                      <div style={{ fontSize:20, fontWeight:700, color:"#F59E0B", fontFamily:"'IBM Plex Mono', monospace" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Layer 2: Rate Reference Affiliate Note ── */}
              <div style={{ marginTop: -8, marginBottom: 16, padding: "10px 16px", background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.12)", borderRadius: 8 }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#64748B" }}>
                  💡 Rates change annually across all 50 states. Payroll platforms like{" "}
                  <a href="https://gusto.pxf.io/X4nKG5" target="_blank" rel="noopener noreferrer sponsored"
                    style={{ color: "#F59E0B", textDecoration: "none", fontWeight: 600 }}>
                    Gusto
                  </a>
                  {" "}and{" "}
                  <a href="https://homebase.sjv.io/c/7263305/3856641/20049" target="_blank" rel="noopener noreferrer sponsored"
                    style={{ color: "#F59E0B", textDecoration: "none", fontWeight: 600 }}>
                    Homebase
                  </a>
                  {" "}automatically apply updated tipped wage rates when laws change — so your payroll stays compliant without manual tracking.
                </span>
              </div>

              {/* Check items */}
              <div style={sectionStyle}>
                <div style={sectionTitle}>Compliance Breakdown</div>
                {result.checks.map(c => <CheckItem key={c.id} item={c} />)}
              </div>

              {/* ── Layer 3: Disclaimer with Resource Mentions ── */}
              <div style={{ padding:"16px 20px", background:"#141B2D", border:"1px solid #1E293B", borderRadius:12, marginTop:4 }}>
                <p style={{ margin:"0 0 8px", fontSize:11, color:"#475569", fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
                  <strong style={{ color:"#64748B" }}>Disclaimer:</strong> Rates reflect available 2026 data and should be verified against your state's Department of Labor. Wage laws change frequently — confirm effective dates for your jurisdiction. This tool is for informational purposes only and does not constitute legal advice. Always consult a licensed employment attorney for compliance decisions.
                </p>
                <p style={{ margin:0, fontSize:11, color:"#475569", fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.7 }}>
                  <strong style={{ color:"#64748B" }}>Automate this:</strong> For automated tip credit tracking, makeup pay calculations, and wage rate updates, payroll platforms like{" "}
                  <a href="https://gusto.pxf.io/X4nKG5" target="_blank" rel="noopener noreferrer sponsored" style={{ color:"#64748B", textDecoration:"underline" }}>Gusto</a>,{" "}
                  <a href="https://homebase.sjv.io/c/7263305/3856641/20049" target="_blank" rel="noopener noreferrer sponsored" style={{ color:"#64748B", textDecoration:"underline" }}>Homebase</a>
                  handle this math every pay period. Some links on this page are affiliate links — we may earn a commission at no cost to you if you sign up.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
